import { cloneDeep, isEmpty } from 'lodash';
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Loader, Selector } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { MORE_LEVELS } from 'sonata-static-storage/lib/public/catalogs/optimizationModel.catalog';

import {
  fetchOptimizationModel,
  fetchOptimizationModelKpiCatalog,
  fetchOptimizationModels
} from '@/services/optimizationModel.service';
import { SonataMOREContext } from '../SonataMORE.context';
import { SonataMORETooltip } from '../SonataMORETooltip';

import './SonataMORESettings.styles.scss';

import type { OptimizationModelInterface } from 'sonata-static-storage/lib/schemas/campaign/optimizationModel.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { CurrencyInfo } from '../SonataMORE.types';

type SonataMORESettingsProps = {
  campaignCountries: string[];
  currencyInfo: CurrencyInfo;
  defaultPacingDistribution: number;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
};

function getOptimizationModelKpiLevel(selectedOptimizationModel: Record<string, any>): string | undefined {
  const {
    datasource: { kpis: dataSourceKpis },
    kpi: selectedModelKpi
  } = selectedOptimizationModel;
  const dataSourceKpi = (dataSourceKpis as Array<Record<string, any>>).find(entry => entry.name === selectedModelKpi);
  const associatedKpiEvent = dataSourceKpi?.goalEvent;
  return Object.keys(MORE_LEVELS).find(level =>
    (MORE_LEVELS as Record<string, string[]>)[level].includes(associatedKpiEvent)
  );
}

function filterOptimizationModelsByLevel(models: Record<string, any>[], level = ''): Record<string, any>[] {
  const validEvents = (MORE_LEVELS as Record<string, string[]>)[level] ?? [];
  return models
    .filter(model => {
      const {
        datasource: { kpis },
        kpi: modelKpi
      } = model;
      const dataSourceKpi = (kpis as Array<Record<string, any>>).find(entry => entry.name === modelKpi);
      return validEvents.includes(dataSourceKpi?.goalEvent);
    })
    .sort((a, b) => b.version - a.version);
}

function getCampaignOptimizationModelFromSelectedModel(
  selectedModel: Record<string, any>,
  defaultPacingDistribution: number,
  currentCampaignOptimizationModel?: OptimizationModelInterface
): OptimizationModelInterface {
  const { id } = selectedModel;
  return {
    distribution: {
      distribution: defaultPacingDistribution,
      enabled: false,
      ...(currentCampaignOptimizationModel ? currentCampaignOptimizationModel.distribution : {})
    },
    id
  };
}

/**
 * KPI category + KPI selectors with the info tooltip button.
 * Manages data fetching for optimization models and KPI catalog via the shared context.
 */
function SonataMORESettings({
  campaignCountries,
  currencyInfo: _currencyInfo,
  defaultPacingDistribution,
  user,
  userRoles
}: SonataMORESettingsProps) {
  const { formatMessage } = useIntl();

  const {
    currentCampaignOptimizationModel,
    currentSelectedOptimizationModel,
    models,
    optimizationModelKpiCatalog,
    optimizationModelKpiLevel,
    selectedOptimizationModelKpi,
    setContext
  } = useContext(SonataMOREContext);

  const tokens: {
    kpiCatalog: Record<string, any>;
    model: Record<string, any>;
    models: Record<string, any>;
  } = {
    kpiCatalog: {},
    model: {},
    models: {}
  };

  const mloPermission = user && userRoles && checkUserResourcePermissions('campaignOptimization', user, userRoles);
  const advancedSettingsPermissions = checkUserResourcePermissions('campaignAdvancedOptimization', user, userRoles);

  useEffect(() => {
    if (!mloPermission) {
      return;
    }

    const fetchData = async () => {
      if (!optimizationModelKpiCatalog?.length) {
        const newOptimizationModelKpiCatalog = await fetchOptimizationModelKpiCatalog(tokens.kpiCatalog);
        setContext(prevContext => ({
          ...prevContext,
          optimizationModelKpiCatalog: newOptimizationModelKpiCatalog
        }));
      }

      if (!models.length && !isEmpty(campaignCountries)) {
        const newModels = await fetchOptimizationModels(campaignCountries, tokens.models);
        setContext(prevContext => ({
          ...prevContext,
          models: newModels
        }));
      }

      if (isEmpty(currentSelectedOptimizationModel) && !isEmpty(currentCampaignOptimizationModel)) {
        const newSelectedOptimizationModel = await fetchOptimizationModel(
          currentCampaignOptimizationModel!.id,
          currentCampaignOptimizationModel?.version,
          tokens.model
        );
        setContext(prevContext => ({
          ...prevContext,
          currentSelectedOptimizationModel: newSelectedOptimizationModel,
          optimizationModelKpiLevel: getOptimizationModelKpiLevel(newSelectedOptimizationModel),
          selectedOptimizationModelKpi: newSelectedOptimizationModel?.kpi
        }));
      }
    };

    fetchData();

    return () => {
      Object.values(tokens).forEach((requestToken: Record<string, any>) => {
        requestToken.cancel?.();
      });
    };
  }, [
    campaignCountries,
    currentCampaignOptimizationModel,
    currentSelectedOptimizationModel,
    mloPermission,
    models.length,
    optimizationModelKpiCatalog?.length,
    setContext
  ]);

  const optimizationModelsForUser = !advancedSettingsPermissions
    ? models.filter(model => {
        const omKpi = model.kpi;
        const maxVersion = Math.max(...models.filter(e => e.kpi === omKpi).map(e => e.version));
        return model.version === maxVersion;
      })
    : models;

  const handleKPILevelSelectionChange = (selectedOption: Record<string, any>) => {
    if (optimizationModelKpiLevel !== selectedOption.id) {
      setContext(prevContext => ({
        ...prevContext,
        currentCampaignOptimizationModel: undefined,
        currentSelectedOptimizationModel: undefined,
        optimizationModelKpiLevel: selectedOption.id,
        selectedOptimizationModelKpi: undefined
      }));
    }
  };

  const handleKPISelectionChange = (selectedOption: Record<string, any>) => {
    if (selectedOptimizationModelKpi !== selectedOption.id) {
      const validOptimizationModels = filterOptimizationModelsByLevel(
        optimizationModelsForUser,
        optimizationModelKpiLevel!
      );
      const model = validOptimizationModels.find(om => om.kpi === selectedOption.value);
      const newModel = cloneDeep(model);
      delete newModel?.version;
      const newCampaignOptimizationModel = getCampaignOptimizationModelFromSelectedModel(
        newModel!,
        defaultPacingDistribution,
        currentCampaignOptimizationModel
      );
      setContext(prevContext => ({
        ...prevContext,
        currentCampaignOptimizationModel: newCampaignOptimizationModel,
        currentSelectedOptimizationModel: newModel,
        selectedOptimizationModelKpi: newModel?.kpi
      }));
    }
  };

  const optimizationModelKpiLevelsCatalog = Object.keys(MORE_LEVELS).map(level => ({
    id: level,
    label: formatMessage({
      id: `optimizationModel.levels.${level}`
    })
  }));

  const getUniqKpiSelectorOptions = () => {
    const optionsFilteredByLevel = filterOptimizationModelsByLevel(
      optimizationModelsForUser,
      optimizationModelKpiLevel
    );
    const optionsFilteredByKpi = optionsFilteredByLevel.map(m => m?.kpi);
    const uniqKpiOptions = [...new Set(optionsFilteredByKpi)];
    return uniqKpiOptions.map(option => ({
      id: option,
      label: formatMessage({
        defaultMessage: option?.toUpperCase(),
        id: `optimizationModel.kpi.${option}`
      })
    }));
  };

  const kpiSelectorOptions = !optimizationModelKpiLevel ? [] : getUniqKpiSelectorOptions();

  const optimizationModelLevelValue = optimizationModelKpiLevelsCatalog.filter(
    option => option.id === optimizationModelKpiLevel
  );
  const optimizationModelKpiValue = kpiSelectorOptions.filter(
    option => option.id === currentSelectedOptimizationModel?.kpi
  );

  const renderSpinner = () => {
    if (optimizationModelKpiLevel && isEmpty(kpiSelectorOptions)) {
      return <Loader showBackground type="landing" variant="default" />;
    }
    return null;
  };

  return (
    <div className="sonataMORESettings">
      {isEmpty(models) ? (
        <Loader showBackground type="landing" variant="default" />
      ) : (
        <div className="sonataMORESettings__selectors">
          <Selector
            className="basic-single small sonataMORESettings__levelSelector"
            onChange={handleKPILevelSelectionChange}
            options={optimizationModelKpiLevelsCatalog}
            placeholder={formatMessage({
              defaultMessage: 'Select the KPI category',
              id: 'optimizationModel.simpleSettings.kpiLevelSearchPlaceholder'
            })}
            tiny
            type="simple"
            value={optimizationModelLevelValue}
          />
          {renderSpinner()}
          <Selector
            className="basic-single small sonataMORESettings__kpiSelector"
            isDisabled={typeof optimizationModelKpiLevel === 'undefined'}
            onChange={handleKPISelectionChange}
            options={kpiSelectorOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select KPI',
              id: 'optimizationModel.simpleSettings.searchPlaceholder'
            })}
            tiny
            type="simple"
            value={optimizationModelKpiValue}
          />
          {currentSelectedOptimizationModel && (
            <SonataMORETooltip optimizationModel={currentSelectedOptimizationModel} user={user} userRoles={userRoles} />
          )}
        </div>
      )}
    </div>
  );
}

export default SonataMORESettings;
